(() => {
  if (customElements.get('fx-lifestyle-story')) return;

  class FxLifestyleStory extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.tabs = Array.from(this.querySelectorAll('[data-story-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-story-panel]'));
      if (!this.tabs.length || !this.panels.length) return;

      this.activeStoryIndex = Math.max(0, this.tabs.findIndex((tab) => tab.classList.contains('is-active')));
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = Math.max(4000, Number.parseInt(this.dataset.interval, 10) || 5000);
      this.pauseReasons = new Set();
      this.timer = null;
      this.preloadTimer = null;
      this.touchStart = null;
      this.abortController = new AbortController();
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      // Desktop shows every story as a stacking card instead of tabs.
      this.stackQuery = window.matchMedia('(min-width: 990px)');
      this.stackFrame = null;
      this.fxPageStops = () => this.getStackStops();

      this.bindEvents();
      this.prepareControls();
      this.applyLayoutMode();
      this.observeVisibility();
      this.setPauseReason('reduced-motion', this.reducedMotionQuery.matches);
      this.setPauseReason('document-hidden', document.hidden);

      const activePanel = this.panels[this.activeStoryIndex];
      this.setSlide(activePanel, Number.parseInt(activePanel.dataset.activeSlide, 10) || 0, false);
      this.syncTimer();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.preloadTimer);
      window.cancelAnimationFrame(this.stackFrame);
      this.headingObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.abortController?.abort();
      delete this.dataset.initialized;
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-story-tab]');
        if (tab) {
          this.showStory(Number.parseInt(tab.dataset.storyIndex, 10));
          return;
        }

        const panel = event.target.closest('[data-story-panel]');
        if (!panel) return;

        if (event.target.closest('[data-story-previous]')) {
          this.stepSlide(panel, -1, true);
          return;
        }

        if (event.target.closest('[data-story-next]')) {
          this.stepSlide(panel, 1, true);
          return;
        }

        const dot = event.target.closest('[data-story-dot]');
        if (dot) this.setSlide(panel, Number.parseInt(dot.dataset.slideIndex, 10), true);
      }, { signal });

      this.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[data-story-tab]');
        if (!tab) return;

        const currentIndex = Number.parseInt(tab.dataset.storyIndex, 10);
        let nextIndex = null;

        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.tabs.length - 1;

        if (nextIndex === null) return;
        event.preventDefault();
        this.tabs[nextIndex].focus();
        this.showStory(nextIndex);
      }, { signal });

      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.setPauseReason('hover', true);
      }, { signal });

      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.setPauseReason('hover', false);
      }, { signal });

      this.addEventListener('focusin', () => this.setPauseReason('focus', true), { signal });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setPauseReason('focus', false);
      }, { signal });

      this.addEventListener('touchstart', (event) => {
        if (event.target.closest('a, button') || event.touches.length !== 1) return;
        const touch = event.touches[0];
        this.touchStart = { x: touch.clientX, y: touch.clientY };
      }, { passive: true, signal });

      this.addEventListener('touchend', (event) => {
        if (!this.touchStart || event.changedTouches.length !== 1) return;
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStart.x;
        const deltaY = touch.clientY - this.touchStart.y;
        this.touchStart = null;

        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
        const activePanel = this.panels[this.activeStoryIndex];
        this.stepSlide(activePanel, deltaX < 0 ? 1 : -1, true);
      }, { passive: true, signal });

      document.addEventListener('visibilitychange', () => {
        this.setPauseReason('document-hidden', document.hidden);
      }, { signal });

      this.reducedMotionQuery.addEventListener('change', (event) => {
        this.setPauseReason('reduced-motion', event.matches);
      }, { signal });

      this.stackQuery.addEventListener('change', () => this.applyLayoutMode(), { signal });

      const queueStack = () => {
        if (!this.isStacked() || this.stackFrame) return;
        this.stackFrame = window.requestAnimationFrame(() => {
          this.stackFrame = null;
          this.updateStack();
        });
      };
      window.addEventListener('scroll', queueStack, { passive: true, signal });
      window.addEventListener('resize', queueStack, { passive: true, signal });

      this.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('[data-story-panel]') || event.target.querySelector?.('[data-story-panel]');
        if (!panel) return;
        this.showStory(Number.parseInt(panel.dataset.storyIndex, 10));
        if (this.isStacked()) {
          const stop = this.getStackStops()[Number.parseInt(panel.dataset.storyIndex, 10)];
          if (stop !== undefined) window.scrollTo({ top: stop, behavior: 'smooth' });
        }
        const selectedSlide = event.target.closest('[data-story-slide]');
        if (selectedSlide) {
          const slides = this.getSlides(panel);
          const slideIndex = slides.indexOf(selectedSlide);
          if (slideIndex >= 0) this.setSlide(panel, slideIndex, true);
        }
        this.setPauseReason('theme-editor', true);
      }, { signal });

      this.addEventListener('shopify:block:deselect', () => {
        this.setPauseReason('theme-editor', false);
      }, { signal });
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.visibilityObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.setPauseReason('offscreen', !entry?.isIntersecting);
      }, { threshold: 0.18 });

      this.visibilityObserver.observe(this);
    }

    prepareControls() {
      this.panels.forEach((panel) => {
        const slides = this.getSlides(panel);
        const controls = panel.querySelector('[data-story-controls]');
        const dotsContainer = panel.querySelector('[data-story-dots]');
        if (!controls || !dotsContainer) return;

        controls.hidden = slides.length < 2;
        dotsContainer.replaceChildren();

        slides.forEach((slide, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = `fx-lifestyle-dot${index === 0 ? ' is-active' : ''}`;
          dot.dataset.storyDot = '';
          dot.dataset.slideIndex = String(index);
          dot.setAttribute('aria-label', `Show image ${index + 1} of ${slides.length}`);
          dot.setAttribute('aria-current', String(index === 0));
          dotsContainer.append(dot);
        });
      });
    }

    syncPanels() {
      const stacked = this.isStacked();

      this.tabs.forEach((tab, index) => {
        const active = index === this.activeStoryIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel, index) => {
        const active = index === this.activeStoryIndex;
        panel.hidden = stacked ? false : !active;
        panel.classList.toggle('is-active', active);
      });
    }

    isStacked() {
      return this.stackQuery.matches && this.panels.length > 1;
    }

    applyLayoutMode() {
      const stacked = this.isStacked();
      this.classList.toggle('is-stacked', stacked);
      this.syncStackHeading(stacked);

      this.panels.forEach((panel, index) => {
        if (stacked) {
          panel.style.setProperty('--fx-stack-index', String(index));
          // Every card is on screen at some point, so load its current image.
          this.loadSlide(this.getSlides(panel)[Number.parseInt(panel.dataset.activeSlide, 10) || 0]);
        } else {
          panel.style.removeProperty('--fx-stack-index');
          panel.style.removeProperty('--fx-stack-cover');
        }
      });

      this.syncPanels();
      if (stacked) this.updateStack();
    }

    // In stacked mode the section heading is pinned and the cards stack below it.
    // Its height sizes the cards so heading + first card fill exactly one screen.
    syncStackHeading(stacked) {
      const section = this.closest('.fx-lifestyle-section');
      const heading = section?.querySelector('.fx-lifestyle-heading');
      this.headingObserver?.disconnect();
      this.headingObserver = null;
      if (!section) return;

      section.classList.toggle('has-stacked-story', stacked && Boolean(heading));
      if (!stacked || !heading) {
        section.style.removeProperty('--fx-life-head-h');
        return;
      }

      const setHeight = () => {
        section.style.setProperty('--fx-life-head-h', `${heading.offsetHeight}px`);
        this.updateStack();
      };
      setHeight();
      if ('ResizeObserver' in window) {
        this.headingObserver = new ResizeObserver(setHeight);
        this.headingObserver.observe(heading);
      }
    }

    // Scroll position at which each card reaches its sticky top. Cards are the
    // only in-flow children in stacked mode, so their natural positions follow
    // from the container top, the card height and the gap between cards.
    getStackStops() {
      if (!this.isStacked()) return [];
      const containerTop = this.getBoundingClientRect().top + window.scrollY;
      const paddingTop = Number.parseFloat(getComputedStyle(this).paddingTop) || 0;

      return this.panels.map((panel, index) => {
        const style = getComputedStyle(panel);
        const pitch = panel.offsetHeight + (Number.parseFloat(style.marginBottom) || 0);
        return containerTop + paddingTop + index * pitch - (Number.parseFloat(style.top) || 0);
      });
    }

    getStackDistances() {
      const scrollY = window.scrollY;
      return this.getStackStops().map((stop) => Math.max(0, stop - scrollY));
    }

    updateStack() {
      if (!this.isStacked()) return;

      const cardHeight = this.panels[0].offsetHeight || window.innerHeight;
      const distances = this.getStackDistances();
      const arrived = distances.map((distance) => Math.min(Math.max(1 - distance / cardHeight, 0), 1));

      // A card recedes a little for every card stacked on top of it.
      this.panels.forEach((panel, index) => {
        const cover = arrived.slice(index + 1).reduce((sum, value) => sum + value, 0);
        panel.style.setProperty('--fx-stack-cover', cover.toFixed(3));
      });

      let active = 0;
      arrived.forEach((value, index) => {
        if (value >= 0.5) active = index;
      });

      if (active !== this.activeStoryIndex) {
        this.activeStoryIndex = active;
        this.syncPanels();
        const panel = this.panels[active];
        this.setSlide(panel, Number.parseInt(panel.dataset.activeSlide, 10) || 0, false);
        this.syncTimer();
      }
    }

    showStory(index) {
      if (!Number.isFinite(index) || !this.panels[index]) return;

      this.activeStoryIndex = index;
      this.syncPanels();

      const panel = this.panels[index];
      const currentSlide = Number.parseInt(panel.dataset.activeSlide, 10) || 0;
      this.setSlide(panel, currentSlide, false);
      this.syncTimer();
    }

    stepSlide(panel, direction, userInitiated) {
      const slides = this.getSlides(panel);
      if (slides.length < 2) return;

      const currentIndex = Number.parseInt(panel.dataset.activeSlide, 10) || 0;
      const nextIndex = (currentIndex + direction + slides.length) % slides.length;
      this.setSlide(panel, nextIndex, userInitiated);
    }

    setSlide(panel, requestedIndex, userInitiated) {
      if (!panel) return;

      const slides = this.getSlides(panel);
      if (!slides.length) return;

      const index = Math.min(Math.max(requestedIndex, 0), slides.length - 1);
      const dots = Array.from(panel.querySelectorAll('[data-story-dot]'));

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });

      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });

      panel.dataset.activeSlide = String(index);
      this.loadSlide(slides[index]);
      this.schedulePreload(slides[(index + 1) % slides.length]);

      if (userInitiated) this.syncTimer();
    }

    getSlides(panel) {
      return Array.from(panel?.querySelectorAll('[data-story-slide]') || []);
    }

    loadSlide(slide) {
      slide?.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        delete source.dataset.srcset;
      });

      const image = slide?.querySelector('img[data-src], img[src]');
      if (!image) return;

      if (image.dataset.srcset) {
        image.srcset = image.dataset.srcset;
        delete image.dataset.srcset;
      }

      if (image.dataset.src) {
        image.src = image.dataset.src;
        delete image.dataset.src;
      }
    }

    schedulePreload(slide) {
      window.clearTimeout(this.preloadTimer);
      if (!slide) return;

      this.preloadTimer = window.setTimeout(() => this.loadSlide(slide), 1200);
    }

    setPauseReason(reason, paused) {
      if (paused) {
        this.pauseReasons.add(reason);
      } else {
        this.pauseReasons.delete(reason);
      }
      this.syncTimer();
    }

    syncTimer() {
      window.clearTimeout(this.timer);
      this.timer = null;

      if (!this.autoplay || this.pauseReasons.size > 0) return;

      const activePanel = this.panels?.[this.activeStoryIndex];
      if (this.getSlides(activePanel).length < 2) return;

      this.timer = window.setTimeout(() => {
        this.stepSlide(activePanel, 1, false);
        this.syncTimer();
      }, this.interval);
    }
  }

  customElements.define('fx-lifestyle-story', FxLifestyleStory);
})();
